import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { comics } from '@/mocks/comics';

type SearchBoxProps = {
  initialValue?: string;
  placeholder?: string;
  size?: 'md' | 'lg';
  onSearch?: (keyword: string) => void;
};

export default function SearchBox({
  initialValue = '',
  placeholder = '搜索漫画名、作者或题材…',
  size = 'md',
  onSearch,
}: SearchBoxProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = useMemo(() => {
    const keyword = value.trim().toLowerCase();
    if (!keyword) return [];
    return comics
      .filter(
        (comic) =>
          comic.title.toLowerCase().includes(keyword) ||
          comic.author.toLowerCase().includes(keyword) ||
          comic.tags.some((tag) => tag.toLowerCase().includes(keyword)),
      )
      .slice(0, 6);
  }, [value]);

  const submit = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setOpen(false);
    if (onSearch) {
      onSearch(trimmed);
    } else {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const heightClass = size === 'lg' ? 'py-3.5' : 'py-2.5';

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit(value);
        }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
          <input
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            aria-label="搜索漫画"
            className={`w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200 ${heightClass}`}
          />
        </div>
        <button
          type="submit"
          className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 cursor-pointer"
        >
          搜索
        </button>
      </form>

      {open && value.trim() && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm">
          {suggestions.length > 0 ? (
            suggestions.map((comic) => (
              <button
                key={comic.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/detail/${comic.id}`);
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-primary-50 cursor-pointer"
              >
                <span className="h-10 w-8 shrink-0 overflow-hidden rounded">
                  <img src={comic.cover} alt={comic.title} className="h-full w-full object-top" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground-900">{comic.title}</span>
                  <span className="block truncate text-xs text-foreground-500">{comic.author}</span>
                </span>
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-center text-xs text-foreground-500">
              没有找到相关漫画，试试其它关键词
            </div>
          )}
        </div>
      )}
    </div>
  );
}